import { memo, useCallback, useState } from 'react';

import { useTrackRestoreBoard } from '@air/analytics';
import { type Board } from '@air/api/types';
import { ConfirmationModal } from '@air/component-confirmation-modal';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';

import {
  type NewParentLocation,
  useBoardRestorationTask,
} from '~/components/RecentlyDeleted/hooks/useBoardRestorationTask';
import { RecentlyDeletedNewParentSelectionModal } from './RecentlyDeletedNewParentSelectionModal';

export type RecentlyDeletedRecoverBoardModalParams = {
  id: Board['id'];
  title: string;
  hasDeletedParent: boolean;
};

export const RecentlyDeletedRecoverBoardModal = memo(
  ({ id, onClose, title, hasDeletedParent }: AirModalProps<RecentlyDeletedRecoverBoardModalParams>) => {
    const { createBoardRestorationTask } = useBoardRestorationTask();
    const [showRecentlyDeletedNewParentSelectionModal] = useAirModal(RecentlyDeletedNewParentSelectionModal);
    const { trackRestoreBoard } = useTrackRestoreBoard();
    const [loading, setLoading] = useState(false);
    const { showToast } = useToasts();

    const handleRecover = useCallback(
      // Parameter parent if `root` then will restore as a root board.
      // If `undefined` then will try to restore the board to it's original parent.
      // If `board.id` then will restore the board inside the selected parent.
      async (parent?: NewParentLocation) => {
        setLoading(true);
        const result = await createBoardRestorationTask({
          boardId: id,
          title,
          parent,
        });
        if (result.data) {
          trackRestoreBoard({
            board_id: id,
            board_name: title,
          });
          onClose();
        } else if (result.error) {
          showToast(result.error.message || 'There was an error restoring the board.');
        }
        setLoading(false);
      },
      [createBoardRestorationTask, id, onClose, showToast, title, trackRestoreBoard],
    );

    const handleConfirm = useCallback(async () => {
      if (hasDeletedParent) {
        showRecentlyDeletedNewParentSelectionModal({
          onConfirm: handleRecover,
          canSelectLibrary: true,
          description: (
            <>
              <p>The original location of this board has been modified or no longer exists.</p>
              <p className="mt-4">Select a new location to recover this board to</p>
            </>
          ),
          rootOptionLabel: 'Move board to top level',
        });
        onClose();
      } else {
        // Try to restore the board to it's original parent.
        await handleRecover();
      }
    }, [handleRecover, hasDeletedParent, onClose, showRecentlyDeletedNewParentSelectionModal]);

    return (
      <ConfirmationModal
        id="recently-deleted-recover-board"
        ctaPrimaryText="Recover"
        description={`Recovering “${title}” will also restore any sub-boards and assets that were deleted within it.`}
        isOpen
        onDismiss={onClose}
        onConfirm={handleConfirm}
        isLoading={loading}
        isDisabled={loading}
        title="Recover board?"
      />
    );
  },
);

RecentlyDeletedRecoverBoardModal.displayName = 'RecentlyDeletedRecoverBoardModal';
